// WebCodecs demux -> decode frame source (Local Engine Phase 3).
// Replaces HTMLVideoElement seeking with an mp4box.js demux + VideoDecoder pipeline:
// frame-accurate, in presentation order, no per-frame seek latency. Streaming with a
// bounded reorder buffer so memory stays low. Falls back to seeking (in localExport)
// if anything here throws or the container isn't demuxable.

import { createFile, DataStream } from "mp4box";

// Build the avcC/hvcC decoder description from an mp4box track.
function descriptionFor(file, track) {
  const trak = file.getTrackById(track.id);
  for (const entry of trak.mdia.minf.stbl.stsd.entries) {
    const box = entry.avcC || entry.hvcC || entry.vpcC || entry.av1C;
    if (box) {
      const stream = new DataStream(undefined, 0, DataStream.BIG_ENDIAN);
      box.write(stream);
      return new Uint8Array(stream.buffer, 8); // strip the box header
    }
  }
  return null;
}

// Returns an async frame source: { next(): Promise<VideoFrame|null>, close(), width, height, fps, nbFrames }
export async function createDemuxFrameSource(url, onLoad) {
  const resp = await fetch(url);
  let buf;
  const total = Number(resp.headers.get("content-length")) || 0;
  if (onLoad && total && resp.body) {
    const parts = [];
    const reader = resp.body.getReader();
    let got = 0;
    for (;;) {
      const { done, value } = await reader.read();
      if (done) break;
      parts.push(value); got += value.length;
      onLoad(Math.min(1, got / total));
    }
    const out = new Uint8Array(got);
    let off = 0;
    for (const p of parts) { out.set(p, off); off += p.length; }
    buf = out.buffer;
  } else {
    buf = await resp.arrayBuffer();
  }
  const file = createFile();

  const chunks = [];
  file.onSamples = (id, user, samples) => {
    for (const s of samples) {
      chunks.push(new window.EncodedVideoChunk({
        type: s.is_sync ? "key" : "delta",
        timestamp: (s.cts / s.timescale) * 1_000_000,
        duration: (s.duration / s.timescale) * 1_000_000,
        data: s.data,
      }));
    }
  };
  // mp4box 2.x only emits samples when extraction is started from inside onReady.
  const ready = new Promise((resolve, reject) => {
    file.onError = (e) => reject(new Error("mp4box: " + e));
    file.onReady = (info) => {
      const track = info.videoTracks && info.videoTracks[0];
      if (!track) return reject(new Error("No video track to demux."));
      file.setExtractionOptions(track.id, null, { nbSamples: Infinity });
      file.start();
      resolve({ info, track });
    };
  });
  const ab = buf; ab.fileStart = 0;
  file.appendBuffer(ab);
  file.flush();
  const { track } = await ready;

  const codec = track.codec;
  const description = descriptionFor(file, track);
  const width = track.track_width || track.video?.width;
  const height = track.track_height || track.video?.height;
  const nbFrames = track.nb_samples;
  const durSec = track.duration / track.timescale;
  const fps = nbFrames && durSec ? nbFrames / durSec : 30;

  // Reorder buffer keyed by timestamp (handles B-frame output reordering).
  const frames = [];
  let waiter = null;
  let decodeErr = null;
  let ended = false;

  const decoder = new window.VideoDecoder({
    output: (frame) => { frames.push(frame); if (waiter) { const w = waiter; waiter = null; w(); } },
    error: (e) => { decodeErr = e; if (waiter) { const w = waiter; waiter = null; w(); } },
  });
  const cfg = { codec, codedWidth: width, codedHeight: height };
  if (description) cfg.description = description;
  decoder.configure(cfg);

  if (!chunks.length) throw new Error("NO_SAMPLES");

  // Feed decoder progressively; keep queue bounded.
  let fed = 0;
  async function pump() {
    while (fed < chunks.length) {
      // Bound both the input queue and decoded-but-unconsumed frames (hardware decoders stall when
      // their frame pool is exhausted, which used to drop the picture from long exports).
      if (decoder.decodeQueueSize > 16 || frames.length > 12) { await new Promise((r) => setTimeout(r, 4)); continue; }
      decoder.decode(chunks[fed++]);
    }
    await decoder.flush();
    ended = true;
    if (waiter) { const w = waiter; waiter = null; w(); }
  }
  pump().catch((e) => { decodeErr = e; });

  return {
    codec, width, height, fps, nbFrames,
    async next() {
      while (!frames.length && !ended && !decodeErr) {
        await new Promise((r) => { waiter = r; });
      }
      if (decodeErr) throw decodeErr;
      if (!frames.length) return null;
      // return earliest-timestamp frame (reorder safety)
      let mi = 0;
      for (let i = 1; i < frames.length; i++) if (frames[i].timestamp < frames[mi].timestamp) mi = i;
      return frames.splice(mi, 1)[0];
    },
    close() {
      try { decoder.close(); } catch { /* ignore */ }
      for (const f of frames) { try { f.close(); } catch { /* ignore */ } }
      frames.length = 0;
    },
  };
}
